import { useState } from 'react';
import { useQuery } from 'hooks/useCustomQuery';
import { axios } from 'services/axios';
import FormBase from '@/components/forms/new/FormBase';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import InternationalRateNameField from './formFields/InternationalRateNameField';
import InternationalRateStatusField from './formFields/InternationalRateStatusField';
import InternationalRateWholesalerField from './formFields/InternationalRateWholesalerField';
import InternationalRatePartnerField from './formFields/InternationalRatePartnerField';

const EditDetailsForm = ({
    selectedChild,
    darkMode,
    requiredScope,
    wholesalerOptions,
    partnerOptions,
    setPartnerOptions,
    onSubmit,
    onBack,
}) => {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedWholesalerID, setSelectedWholesalerID] = useState(
        selectedChild?.wholesalerID || null,
    );

    useQuery({
        queryKey: ['intlRateEditPartners', selectedWholesalerID],
        queryFn: async () => {
            const res = await axios.get(`/partners/${selectedWholesalerID}`);
            return (res.data || []).map((p) => ({
                label: p.name,
                value: p.id,
            }));
        },
        enabled: Boolean(selectedWholesalerID),
        onSuccess: (data) => setPartnerOptions(data),
        onError: () => setPartnerOptions([]),
    });

    const initialValues = {
        name: selectedChild?.name ?? '',
        isPublic: selectedChild?.isPublic ?? false,
        isActive: selectedChild?.isActive ?? false,
        wholesalerID: selectedChild?.wholesalerID ?? null,
        wholesaler: selectedChild?.wholesaler ?? '',
        partnerID: selectedChild?.partnerID ?? null,
        partner: selectedChild?.partner ?? '',
    };

    const handleWholesalerChange = async (form, wholesalerID) => {
        form.setFieldValue('wholesalerID', wholesalerID);
        form.setFieldValue('partnerID', null);

        if (!wholesalerID) {
            setPartnerOptions([]);
            setSelectedWholesalerID(null);
            return;
        }

        setSelectedWholesalerID(wholesalerID);
    };

    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);
        try {
            await onSubmit(values);
            // Navigate back on success
            if (onBack) {
                onBack();
            }
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to update International Rate List');
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    return (
        <FormBase
            initialValues={initialValues}
            finalFormSubmit={finalFormSubmit}
            ufBackBtn="back-btn"
            ufSaveBtn="save-btn"
            isLoading={isLoading}
            onBack={onBack}
            customClick={true}
            enableReinitialize={true}
        >
            {({ errors = {}, touched = {}, values = {}, setFieldValue }) => [
                <InternationalRateNameField
                    key="name"
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    darkMode={darkMode}
                />,
                <InternationalRateStatusField
                    key="status"
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    darkMode={darkMode}
                />,
                requiredScope === 80 && (
                    <InternationalRateWholesalerField
                        key="wholesaler"
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                        wholesalerOptions={wholesalerOptions}
                        isLoading={isLoading}
                        darkMode={darkMode}
                        onChange={handleWholesalerChange}
                    />
                ),
                (requiredScope === 80 || requiredScope === 60) && (
                    <InternationalRatePartnerField
                        key="partner"
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                        partnerOptions={partnerOptions}
                        isLoading={isLoading}
                        darkMode={darkMode}
                    />
                ),
                error && (
                    <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                        <AlertIcon />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )
            ].filter(Boolean)}
        </FormBase>
    );
};

export default EditDetailsForm;
